import React, { useState } from 'react';
import axios from 'axios';

import { Heading } from '~/components/core';
import LoadingSwitch from '~/components/core/Loading/LoadingSwitch';
import SortableTable from '~/components/core/Tables/SortableTable';
import { checkIconSpecialCell } from '~/components/SystemConfiguration/FeaturesTab';
import { useSysconfig } from '~/components/SystemConfiguration/SystemConfigurationScreen';
import { AsyncSwitch } from '~/components/SystemConfiguration/Tabs/DeveloperTools/sections/VendorAPIProfiles/VendorAPIProfilesTable/AsyncSwitch';
import type { OrganizationModel } from '~/components/types/organization-types';
import useDataFetcher from '~/components/useDataFetcher';
import { reportAxiosError } from '~/Utils';

interface CliveFeatureFlag {
  id: string;
  name: string;
  description: string;
  is_active: boolean;
  date_activated: string;
  is_editable_by_support: boolean;
}

type CliveFeatureFlagsResponse = Record<
  string,
  {
    is_active: boolean;
    date_activated: string;
    display_name: string;
    description: string;
    is_editable_by_support: boolean;
  }
>;

const CliveFeatureFlags: React.FC = () => {
  const { organization } = useSysconfig() as { organization: OrganizationModel };
  const [isSubmitting, setIsSubmitting] = useState(false);
  const {
    isLoading,
    isError,
    data,
    reloadData: reloadFeatureFlags,
  } = useDataFetcher(`/sysconfig/api/v1/organizations/${organization?.id}/service_ai/clive_ff`);
  const featureFlags = data as CliveFeatureFlagsResponse | undefined;

  const handleSwitchChange = async (featureFlag: CliveFeatureFlag, value: boolean): Promise<void> => {
    try {
      setIsSubmitting(true);
      await axios.put(`/sysconfig/api/v1/organizations/${organization?.id}/service_ai/clive_ff`, {
        ff_key: featureFlag.id,
        state: value,
      });
      await reloadFeatureFlags();
    } catch (error) {
      await reportAxiosError(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const columns = [
    {
      id: 'name',
      label: 'Feature Flag',
    },
    {
      id: 'description',
      label: 'Description',
    },
    {
      id: 'date_activated',
      label: 'Date Activated',
    },
    {
      id: 'is_editable_by_support',
      label: 'Editable By Support',
      specialCell: checkIconSpecialCell('is_editable_by_support' satisfies keyof CliveFeatureFlag),
    },
    {
      id: 'is_active',
      label: 'Enabled',
      specialCell: (row: CliveFeatureFlag) => (
        <AsyncSwitch
          checked={row.is_active}
          onChange={(e) => handleSwitchChange(row, e.target.checked)}
          disabled={isSubmitting}
        />
      ),
    },
  ];

  const rows = featureFlags
    ? Object.entries(featureFlags).map(
        ([key, { is_active, date_activated, display_name, description, is_editable_by_support }]) => ({
          // The key stays the row id: handleSwitchChange posts it back as ff_key.
          id: key,
          name: display_name,
          is_active,
          description,
          date_activated,
          is_editable_by_support,
        })
      )
    : [];

  return (
    <div className="min-h-[50vh] bg-white p-40">
      <LoadingSwitch isLoading={isLoading} isError={isError}>
        <Heading variant={Heading.TYPES.H3}>Clive Feature Flags</Heading>
        <div className="mt-20">
          <SortableTable
            columns={columns}
            rows={rows}
            stickyHeader
            emptyStateComponent={<div>No feature flags available</div>}
          />
        </div>
      </LoadingSwitch>
    </div>
  );
};

export default CliveFeatureFlags;
